import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, DollarSign, User, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

export default function MessagesPanel({ token, userId }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      // Update unread count in conversations list
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.conversation_id}/messages`,
        { content: newMessage, message_type: 'message' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Update conversation's last message
      setConversations(prev => prev.map(c => 
        c.conversation_id === selectedConversation.conversation_id 
          ? { 
              ...c, 
              last_message: { 
                content: newMessage, 
                sender_id: userId,
                created_at: new Date().toISOString()
              },
              updated_at: new Date().toISOString()
            } 
          : c
      ));
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (conversation) => {
    return conversation.participants.find(p => p.user_id !== userId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#5DADE2]" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MessageCircle className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No messages yet</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          When you message a deal poster or service provider, your conversations will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[600px] border border-slate-200 rounded-xl overflow-hidden bg-white">
      {/* Conversations List */}
      <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Messages</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conv) => {
            const other = getOtherParticipant(conv);
            return (
              <button
                key={conv.conversation_id}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors border-b border-slate-100 text-left ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'bg-sky-50' : ''
                }`}
                data-testid={`conversation-${conv.conversation_id}`}
              >
                <div className="w-10 h-10 bg-[#5DADE2] rounded-full flex items-center justify-center flex-shrink-0">
                  {other?.picture ? (
                    <img src={other.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-medium">{other?.name?.charAt(0) || '?'}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{other?.name || 'Unknown'}</span>
                    {conv.last_message && (
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {formatTime(conv.last_message.created_at)}
                      </span>
                    )}
                  </div>
                  {conv.deal_title && (
                    <p className="text-xs text-[#5DADE2] truncate">Re: {conv.deal_title}</p>
                  )}
                  {conv.provider_name && (
                    <p className="text-xs text-[#5DADE2] truncate">{conv.provider_name}</p>
                  )}
                  {conv.last_message && (
                    <p className="text-sm text-slate-500 truncate mt-1">
                      {conv.last_message.message_type === 'offer' && '💰 '}
                      {conv.last_message.content}
                    </p>
                  )}
                </div>
                {conv.unread_count > 0 && (
                  <span className="w-5 h-5 bg-[#5DADE2] text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                    {conv.unread_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Messages View */}
      <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-200 flex items-center gap-3">
              <button 
                onClick={() => setSelectedConversation(null)}
                className="md:hidden p-1 hover:bg-slate-100 rounded"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-10 h-10 bg-[#5DADE2] rounded-full flex items-center justify-center">
                {getOtherParticipant(selectedConversation)?.picture ? (
                  <img 
                    src={getOtherParticipant(selectedConversation).picture} 
                    alt="" 
                    className="w-full h-full rounded-full object-cover" 
                  />
                ) : (
                  <span className="text-white font-medium">
                    {getOtherParticipant(selectedConversation)?.name?.charAt(0) || '?'}
                  </span>
                )}
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">
                  {getOtherParticipant(selectedConversation)?.name || 'Unknown'}
                </h4>
                {selectedConversation.deal_title && (
                  <p className="text-sm text-slate-500">Re: {selectedConversation.deal_title}</p>
                )}
                {selectedConversation.provider_name && (
                  <p className="text-sm text-slate-500">{selectedConversation.provider_name}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-[#5DADE2]" />
                </div>
              ) : (
                <>
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === userId;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                          {msg.message_type === 'offer' && msg.offer_amount && (
                            <div className={`mb-2 p-3 rounded-lg ${
                              isOwn ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'
                            }`}>
                              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                                <DollarSign className="h-4 w-4" />
                                Offer
                              </div>
                              <div className="text-2xl font-bold">
                                {formatPrice(msg.offer_amount)}
                              </div>
                            </div>
                          )}
                          <div
                            className={`p-3 rounded-2xl ${
                              isOwn
                                ? 'bg-[#5DADE2] text-white rounded-br-md'
                                : 'bg-white text-slate-900 rounded-bl-md border border-slate-200'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          <p className={`text-xs text-slate-500 mt-1 ${isOwn ? 'text-right' : ''}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#5DADE2] hover:bg-[#4A9BD1]"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
